import { AGENT_URL } from '@/config/env'
import type {
  CreatedEvalCase,
  CreateEvalAssertionInput,
  CreateEvalCaseInput,
  EvalCaseAssertion,
  EvalCaseAssertionsResponse,
  EvalCaseDetail,
  EvalTestCase,
  EvalTestCasesQueryParams,
  PaginatedResponse,
  RelayEvalRunAssertionResultsResponse,
  RelayEvalRunDetail,
  RelayEvalRunsEnqueueResponse,
  RelayEvalRunsListResponse,
  UpdateEvalAssertionInput,
  UpdateEvalCaseInput,
} from './types'

async function fetchWithAuth<T>(
  url: string,
  token: string
): Promise<PaginatedResponse<T>> {
  const response = await fetch(url, {
    headers: { Authorization: token },
  })

  if (!response.ok) {
    const text = await response.text()
    let message = `Request failed: ${response.status}`
    try {
      const parsed = JSON.parse(text)
      message = parsed.error || message
    } catch {
      if (text) message = text
    }
    throw new Error(message)
  }

  const raw = (await response.json()) as Record<string, unknown>
  const rawData = raw?.data
  const data: T[] = Array.isArray(rawData) ? (rawData as T[]) : []
  const pagination = (raw?.pagination ?? {}) as Record<string, unknown>

  return {
    data,
    pagination: {
      limit: typeof pagination.limit === 'number' ? pagination.limit : 25,
      has_more: Boolean(pagination.has_more),
      next_cursor:
        typeof pagination.next_cursor === 'string'
          ? pagination.next_cursor
          : null,
    },
  }
}

async function bodyMutateWithAuth<T>(
  method: 'POST' | 'PATCH',
  url: string,
  token: string,
  body: unknown
): Promise<T> {
  const response = await fetch(url, {
    method,
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  const text = await response.text()
  let parsed: unknown = null
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      parsed = null
    }
  }

  if (!response.ok) {
    const message =
      parsed && typeof parsed === 'object' && 'message' in parsed
        ? String(parsed.message)
        : text || `Request failed: ${response.status}`
    throw new Error(message)
  }

  return parsed as T
}

function postWithAuth<T>(
  url: string,
  token: string,
  body: unknown
): Promise<T> {
  return bodyMutateWithAuth<T>('POST', url, token, body)
}

function patchWithAuth<T>(
  url: string,
  token: string,
  body: unknown
): Promise<T> {
  return bodyMutateWithAuth<T>('PATCH', url, token, body)
}

async function deleteWithAuth(url: string, token: string): Promise<void> {
  const response = await fetch(url, {
    method: 'DELETE',
    headers: { Authorization: token },
  })

  if (!response.ok) {
    const text = await response.text()
    let message = `Request failed: ${response.status}`
    if (text) {
      try {
        const parsed = JSON.parse(text)
        message =
          parsed && typeof parsed === 'object' && 'message' in parsed
            ? String(parsed.message)
            : text
      } catch {
        message = text
      }
    }
    throw new Error(message)
  }
}

async function getJsonWithAuth<T>(url: string, token: string): Promise<T> {
  const response = await fetch(url, {
    headers: { Authorization: token },
  })

  const text = await response.text()
  let parsed: unknown = null
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      parsed = null
    }
  }

  if (!response.ok) {
    const message =
      parsed && typeof parsed === 'object' && 'message' in parsed
        ? String(parsed.message)
        : text || `Request failed: ${response.status}`
    throw new Error(message)
  }

  return parsed as T
}

function buildSearchParams(
  params: Record<string, string | number | undefined>,
  cursor?: string
): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value))
  }
  if (cursor) search.set('cursor', cursor)
  return search.toString()
}

export async function fetchEvalTestCases(
  token: string,
  params: EvalTestCasesQueryParams,
  cursor?: string
): Promise<PaginatedResponse<EvalTestCase>> {
  const qs = buildSearchParams(
    {
      duty_id: params.duty_id,
      limit: params.limit,
      sort_by: params.sort_by,
      sort_order: params.sort_order,
    },
    cursor
  )
  return fetchWithAuth<EvalTestCase>(
    `${AGENT_URL}/data/eval-test-cases?${qs}`,
    token
  )
}

export async function createEvalCase(
  token: string,
  input: CreateEvalCaseInput
): Promise<CreatedEvalCase> {
  const raw = await postWithAuth<Record<string, unknown>>(
    `${AGENT_URL}/evals/cases`,
    token,
    { ...input, channel: 'chat', active: true }
  )
  // Relay may return the case bare or wrapped in an {item}/{case} envelope.
  const item = raw?.item
  const wrapped = raw?.case
  const candidate =
    item && typeof item === 'object'
      ? (item as Record<string, unknown>)
      : wrapped && typeof wrapped === 'object'
        ? (wrapped as Record<string, unknown>)
        : raw
  // Fail loudly rather than returning an id-less object — the caller relies on
  // the id to attach assertions and navigate to the new case.
  if (
    !candidate ||
    typeof candidate.id !== 'string' ||
    candidate.id.trim() === ''
  ) {
    throw new Error('Case was created but the response did not include an id.')
  }
  return candidate as unknown as CreatedEvalCase
}

export async function createEvalAssertion(
  token: string,
  caseId: string,
  input: CreateEvalAssertionInput
): Promise<EvalCaseAssertion> {
  // The Relay assertion endpoint returns the bare created object (no
  // {item}/{case}/{data} envelope), so return it as-is.
  return postWithAuth<EvalCaseAssertion>(
    `${AGENT_URL}/evals/cases/${encodeURIComponent(caseId)}/assertions`,
    token,
    input
  )
}

export async function fetchEvalCaseAssertions(
  token: string,
  caseId: string,
  opts?: { includeInactive?: boolean }
): Promise<EvalCaseAssertionsResponse> {
  const query = opts?.includeInactive ? '?include_inactive=true' : ''
  return getJsonWithAuth<EvalCaseAssertionsResponse>(
    `${AGENT_URL}/evals/cases/${encodeURIComponent(caseId)}/assertions${query}`,
    token
  )
}

export async function fetchEvalCase(
  token: string,
  caseId: string
): Promise<EvalCaseDetail> {
  return getJsonWithAuth<EvalCaseDetail>(
    `${AGENT_URL}/evals/cases/${encodeURIComponent(caseId)}`,
    token
  )
}

export async function patchEvalCase(
  token: string,
  caseId: string,
  input: UpdateEvalCaseInput
): Promise<EvalCaseDetail> {
  return patchWithAuth<EvalCaseDetail>(
    `${AGENT_URL}/evals/cases/${encodeURIComponent(caseId)}`,
    token,
    input
  )
}

export async function updateEvalAssertion(
  token: string,
  caseId: string,
  assertionId: string,
  input: UpdateEvalAssertionInput
): Promise<EvalCaseAssertion> {
  return patchWithAuth<EvalCaseAssertion>(
    `${AGENT_URL}/evals/cases/${encodeURIComponent(caseId)}/assertions/${encodeURIComponent(assertionId)}`,
    token,
    input
  )
}

export async function deleteEvalAssertion(
  token: string,
  caseId: string,
  assertionId: string
): Promise<void> {
  return deleteWithAuth(
    `${AGENT_URL}/evals/cases/${encodeURIComponent(caseId)}/assertions/${encodeURIComponent(assertionId)}`,
    token
  )
}

export async function enqueueEvalCaseRuns(
  token: string,
  caseIds: string[]
): Promise<RelayEvalRunsEnqueueResponse> {
  return postWithAuth<RelayEvalRunsEnqueueResponse>(
    `${AGENT_URL}/evals/runs/enqueue`,
    token,
    { case_ids: caseIds }
  )
}

export async function fetchEvalCaseRuns(
  token: string,
  caseId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<RelayEvalRunsListResponse> {
  const params = new URLSearchParams()
  params.set('case_id', caseId)
  params.set('limit', String(options.limit ?? 25))
  params.set('offset', String(options.offset ?? 0))

  return getJsonWithAuth<RelayEvalRunsListResponse>(
    `${AGENT_URL}/evals/runs?${params.toString()}`,
    token
  )
}

export async function fetchRelayEvalRuns(
  token: string,
  options: { runIds?: string[]; limit?: number; offset?: number } = {}
): Promise<RelayEvalRunsListResponse> {
  const params = new URLSearchParams()
  params.set('limit', String(options.limit ?? 25))
  params.set('offset', String(options.offset ?? 0))
  if (options.runIds?.length) {
    params.set('run_ids', options.runIds.join(','))
  }

  return getJsonWithAuth<RelayEvalRunsListResponse>(
    `${AGENT_URL}/evals/runs?${params.toString()}`,
    token
  )
}

export async function fetchRelayEvalRun(
  token: string,
  runId: string
): Promise<RelayEvalRunDetail> {
  return getJsonWithAuth<RelayEvalRunDetail>(
    `${AGENT_URL}/evals/runs/${encodeURIComponent(runId)}`,
    token
  )
}

export async function fetchEvalRunAssertionResults(
  token: string,
  runId: string
): Promise<RelayEvalRunAssertionResultsResponse> {
  return getJsonWithAuth<RelayEvalRunAssertionResultsResponse>(
    `${AGENT_URL}/evals/runs/${encodeURIComponent(runId)}/assertion-results`,
    token
  )
}
